/**
 * Category Service - API methods for fetching models, styles, and platforms
 * Provides dropdown options for prompt creation and filtering
 */

import { apiService } from './apiService';

export interface CategoryItem {
  id: number;
  type: string;
  name: string;
  description?: string;
}

export type CategoryGroups = Record<string, CategoryItem[]>;

export interface SubmitCategoryPayload {
  type: string;
  name: string;
  description?: string;
}

class CategoryService {
  async getAllCategories(): Promise<CategoryGroups> {
    const response = await apiService.get<CategoryGroups>('/categories');

    if (response.status !== 'success' || !response.data) {
      throw new Error(response.message || 'Failed to fetch categories');
    }

    return this.normalizeGroups(response.data);
  }

  async getCategoryGroups(): Promise<CategoryGroups> {
    return this.getAllCategories();
  }

  async getCategoriesByType(type: string): Promise<CategoryItem[]> {
    const response = await apiService.get<CategoryItem[]>(`/categories/${type}`);

    if (response.status !== 'success' || !response.data) {
      throw new Error(response.message || 'Failed to fetch categories');
    }

    return response.data.map((item) => this.normalizeItem(item, type));
  }

  async submitCategory(payload: SubmitCategoryPayload): Promise<void> {
    const response = await apiService.post('/categories/submit', payload);

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to submit category');
    }
  }

  private normalizeGroups(groups: CategoryGroups): CategoryGroups {
    return Object.entries(groups).reduce<CategoryGroups>((acc, [key, items]) => {
      acc[key] = items.map((item) => this.normalizeItem(item, key));
      return acc;
    }, {} as CategoryGroups);
  }

  private normalizeItem(item: CategoryItem, fallbackType: string): CategoryItem {
    return {
      id: item.id,
      type: item.type || fallbackType,
      name: item.name,
      description: item.description,
    };
  }
}

export const categoryService = new CategoryService();
export default categoryService;